import { Box, Tooltip } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { useId, type ReactElement } from "react";

/**
 * Gives a preference control an explanation that is both a hover tooltip and the
 * control's own `aria-describedby` description.
 *
 * The tooltip title is a node rather than a string so MUI contributes only a
 * description: as a string it becomes an `aria-label` on the wrapping span, a
 * generic element where an accessible name is discarded.
 */

export type ExplainedControlProps = {
  explanation: string;
  /** The control, given the id its `aria-describedby` has to point at. */
  children: (descriptionId: string) => ReactElement;
};

export function ExplainedControl({ explanation, children }: ExplainedControlProps) {
  const descriptionId = useId();

  return (
    <>
      <Tooltip describeChild title={<span>{explanation}</span>}>
        {children(descriptionId)}
      </Tooltip>
      {/*
        Out of flow rather than hidden: `aria-hidden` and `display: none` content
        cannot be referenced by `aria-describedby`.
      */}
      <Box component="span" id={descriptionId} sx={visuallyHidden}>
        {explanation}
      </Box>
    </>
  );
}
